import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { RoundType, RoundTypeWords } from "@heraion/shared";

import type { IconName } from "../Icon/Icon";
import { Badge } from "./Badge";
import type { UiTheme } from "../theme";

// The mark of a deload or test round (#336 S2, brief §7.2: «con glifo e
// testo»). Glyph and word, never colour alone. A normal round has none. Read
// by the phone editor (ladder, lens, heatmap, round menu) and the logger header.

/** The glyph of each marked round type: every mark of a round reads it. */
export const ROUND_TYPE_GLYPH: Record<keyof RoundTypeWords, IconName> = {
  deload: "trend-down",
  test: "target",
};

/** The words of the round types, from the i18n keys
 *  editorV3.roundTypeNormal, roundTypeDeload and roundTypeTest. */
export function useRoundTypeWords(): RoundTypeWords & { normal: string } {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      normal: t("editorV3.roundTypeNormal", { defaultValue: "Normal" }),
      deload: t("editorV3.roundTypeDeload", { defaultValue: "Deload" }),
      test: t("editorV3.roundTypeTest", { defaultValue: "Test" }),
    }),
    [t],
  );
}

/** `compact`: the glyph alone, where a column has no room for the word (the
 *  heatmap's 44pt heads); the word is then its spoken name, and the round
 *  ladder and the lens say it in full. */
export function RoundTypeBadge({
  theme,
  type,
  compact = false,
}: {
  theme: UiTheme;
  type: RoundType;
  compact?: boolean;
}) {
  const words = useRoundTypeWords();
  if (type === "normal") return null;
  const word = words[type];
  return compact ? (
    <Badge theme={theme} icon={ROUND_TYPE_GLYPH[type]} a11yLabel={word} />
  ) : (
    <Badge theme={theme} icon={ROUND_TYPE_GLYPH[type]} label={word} />
  );
}
